import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faImage, 
  faUser, 
  faTimes, 
  faSave,
  faUpload,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import AdminLayout from './AdminLayout';
import { ADMIN_URLS, getResourceUrl } from '../../config/apiConfig';

function AddOfficeBearer() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    bio: '',
    image_url: '',
    linkedin_url: '',
    term_start: '',
    term_end: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, or GIF)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Clear any existing errors
      if (errors.image_url) {
        setErrors({
          ...errors,
          image_url: null
        });
      }
    }
  };
  
  const handleImageUpload = async () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('image', selectedFile);
    
    try {
      const response = await fetch(ADMIN_URLS.OFFICE_BEARER_UPLOAD_IMAGE, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: formDataUpload
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/admin/login', { replace: true });
          return;
        }
        throw new Error('Failed to upload image');
      }
      
      const data = await response.json();
      setFormData({
        ...formData,
        image_url: data.image_url
      });
      
      // Clear the file input and preview since we now have the uploaded image URL
      setSelectedFile(null);
      setImagePreview('');
      
      alert('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(`Failed to upload image: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.position) newErrors.position = 'Position is required';
    if (!formData.bio) newErrors.bio = 'Bio is required';
    if (!formData.image_url && !selectedFile) newErrors.image_url = 'Image is required';
    
    return newErrors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    // Upload image first if file is selected but not uploaded
    if (selectedFile && !formData.image_url) {
      await handleImageUpload();
      if (!formData.image_url) {
        alert('Please upload the image first');
        return;
      }
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(ADMIN_URLS.OFFICE_BEARERS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/admin/login', { replace: true });
          return;
        }
        
        const data = await response.json();
        throw new Error(data.error || 'Failed to create office bearer');
      }
      
      // Success - redirect to office bearers list
      navigate('/admin/office-bearers');
    } catch (error) {
      console.error('Error creating office bearer:', error);
      alert(`Failed to create office bearer: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Add Office Bearer</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard" className="admin-breadcrumb-link">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/office-bearers" className="admin-breadcrumb-link">Office Bearers</Link>
          </li>
          <li className="admin-breadcrumb-item active">Add Office Bearer</li>
        </ul>
      </div>
      
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Office Bearer Information</h2>
        </div>
        <div className="admin-card-body">
          <form onSubmit={handleSubmit} className="admin-form">
            <div className="admin-form-row">
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="name" className="admin-form-label">Name *</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faUser} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.name ? 'admin-input-error' : ''}`}
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
                {errors.name && <div className="admin-error-message">{errors.name}</div>}
              </div>
              
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="position" className="admin-form-label">Position *</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faUser} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="text"
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.position ? 'admin-input-error' : ''}`}
                    style={{ paddingLeft: '35px' }}
                    placeholder="Enter position (e.g., President, Secretary)"
                  />
                </div>
                {errors.position && <div className="admin-error-message">{errors.position}</div>}
              </div>
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="term_start" className="admin-form-label">Term Start Date</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="date"
                    id="term_start"
                    name="term_start"
                    value={formData.term_start}
                    onChange={handleChange}
                    className="admin-form-control"
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
              </div>
              
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="term_end" className="admin-form-label">Term End Date</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="date"
                    id="term_end"
                    name="term_end"
                    value={formData.term_end}
                    onChange={handleChange}
                    className="admin-form-control"
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
                <small className="admin-form-text">Leave empty if currently serving</small>
              </div>
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="image" className="admin-form-label">Image *</label>
              
              <div style={{ marginBottom: '1rem' }}>
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="admin-form-control"
                  style={{ marginBottom: '0.5rem' }}
                />
                <small className="admin-form-text">
                  Supported formats: JPEG, PNG, GIF (Max size: 5MB)
                </small>
              </div>
              
              {selectedFile && (
                <div style={{ marginBottom: '1rem' }}>
                  <button
                    type="button"
                    onClick={handleImageUpload}
                    disabled={isUploading}
                    className="admin-btn admin-btn-secondary admin-btn-with-icon"
                    style={{ marginBottom: '1rem' }}
                  >
                    <FontAwesomeIcon icon={isUploading ? faSpinner : faUpload} spin={isUploading} />
                    <span>{isUploading ? 'Uploading...' : 'Upload Image'}</span>
                  </button>
                </div>
              )}
              
              {errors.image_url && <div className="admin-error-message">{errors.image_url}</div>}
              
              {(imagePreview || formData.image_url) && (
                <div style={{ marginTop: '1rem' }}>
                  <label className="admin-form-label">Image Preview</label>
                  <div style={{
                    width: '150px',
                    height: '150px',
                    borderRadius: '50%',
                    backgroundImage: `url(${imagePreview || getResourceUrl(formData.image_url)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundColor: '#e9ecef',
                    border: '1px solid #ced4da'
                  }}></div>
                </div>
              )}
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="linkedin_url" className="admin-form-label">LinkedIn Profile URL</label>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faUser} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                <input
                  type="url"
                  id="linkedin_url"
                  name="linkedin_url"
                  value={formData.linkedin_url}
                  onChange={handleChange}
                  className="admin-form-control"
                  style={{ paddingLeft: '35px' }}
                  placeholder="https://www.linkedin.com/in/profile-name"
                />
              </div>
              <small className="admin-form-text">Optional: Add LinkedIn profile URL for professional networking</small>
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="bio" className="admin-form-label">Bio *</label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                className={`admin-form-control ${errors.bio ? 'admin-input-error' : ''}`}
                rows="6"
                placeholder="Enter a brief professional bio"
              ></textarea>
              {errors.bio && <div className="admin-error-message">{errors.bio}</div>}
            </div>
            
            <div className="admin-form-actions" style={{ marginTop: '2rem' }}>
              <Link
                to="/admin/office-bearers"
                className="admin-btn admin-btn-light admin-btn-with-icon"
              >
                <FontAwesomeIcon icon={faTimes} />
                <span>Cancel</span>
              </Link>
              <button
                type="submit"
                className="admin-btn admin-btn-primary admin-btn-with-icon"
                disabled={isSubmitting}
              >
                <FontAwesomeIcon icon={faSave} />
                <span>{isSubmitting ? 'Saving...' : 'Save Office Bearer'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AddOfficeBearer;
